import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import type { PurchaseType } from "@/lib/products";
import {
  createLicenseCheckoutSession,
  type CheckoutLineItem,
  type BuyerDetails,
} from "@/utils/payments.functions";

interface LicenseCheckoutProps {
  items: CheckoutLineItem[];
  customerEmail?: string;
  userId?: string;
  buyer?: BuyerDetails;
  returnUrl: string;
  /** Omit for the annual subscription flow; "payment" for one-time purchases. */
  mode?: "subscription" | "payment";
  purchaseType?: PurchaseType;
}

export function LicenseCheckout({
  items,
  customerEmail,
  userId,
  buyer,
  returnUrl,
  mode,
  purchaseType,
}: LicenseCheckoutProps) {
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState("");
  // Create the session once per mount. Stripe rejects a changed client secret,
  // and re-running this on every render would remount the payment form.
  const requested = useRef(false);
  const stripe = useMemo(() => {
    try {
      return getStripe();
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    (async () => {
      try {
        const result = await createLicenseCheckoutSession({
          data: {
            items,
            customerEmail,
            userId,
            buyer,
            returnUrl,
            environment: getStripeEnvironment(),
            ...(mode && { mode }),
            ...(purchaseType && { purchaseType }),
          },
        });
        if ("error" in result) throw new Error(result.error);
        if (!result.clientSecret) throw new Error("Checkout could not be started.");
        setClientSecret(result.clientSecret);
      } catch (err) {
        // Show the real reason here; inside Stripe's frame it would only ever
        // read "Something went wrong".
        setError(
          err instanceof Error && err.message
            ? err.message
            : "We couldn't open the payment form. Please try again.",
        );
      }
    })();
    // Intentionally mount-only: see requested ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="py-6 text-center">
        <p className="text-base font-semibold text-ink">We couldn't open the payment form.</p>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!clientSecret || !stripe) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        Loading secure payment form…
      </div>
    );
  }

  return (
    <div id="checkout">
      <EmbeddedCheckoutProvider stripe={stripe} options={{ clientSecret }}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
